import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_CRITERIA,
  Criteria,
  GET_CRITERIA,
  PATCH_CRITERIA,
} from '@frontend/data-access';
import { Button, Input, List, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { TabHeader } from '../components';
import useTranslation from 'next-translate/useTranslation';

function EvaluationCriteria() {
  const { t } = useTranslation('common');
  const { data, loading, refetch } = useQuery<{ criteria: Criteria[] }>(
    GET_CRITERIA,
    {
      variables: { type: ['CHANNEL'] },
    }
  );
  const [criteria, setCriteria] = useState<
    {
      name: string;
      description: string;
      id: string;
    }[]
  >([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedCriteria, setEditedCriteria] = useState<{
    name?: string;
    description?: string;
  }>();

  const handleCompleted = () => {
    setEditingId(null);
    setEditedCriteria(undefined);
    refetch();
  };
  const [createCriteria, { loading: createLoading }] = useMutation(
    CREATE_CRITERIA,
    { onCompleted: handleCompleted }
  );
  const [patchCriteria, { loading: patchLoading }] = useMutation(
    PATCH_CRITERIA,
    { onCompleted: handleCompleted }
  );

  const handleClickAdd = () => {
    setCriteria([
      ...criteria.filter((i) => i.id !== 'NEW'),
      {
        id: 'NEW',
        name: '',
        description: '',
      },
    ]);
    setEditingId('NEW');
  };

  const handleEdit = (id: string, name: string, description: string) => {
    setEditingId(id);
    setEditedCriteria({ name, description });
  };

  const handleClickCancel = () => {
    setEditingId(null);
    setEditedCriteria(undefined);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editedCriteria) return;

    if (editingId === 'NEW') {
      createCriteria({
        variables: {
          input: {
            name: editedCriteria.name,
            description: editedCriteria.description,
            type: 'CHANNEL',
          },
        },
      });
    } else {
      patchCriteria({
        variables: {
          input: {
            id: editingId,
            name: editedCriteria.name,
            description: editedCriteria.description,
          },
        },
      });
    }
  };

  useEffect(() => {
    setCriteria(data?.criteria || []);
  }, [data?.criteria]);

  if (loading)
    return (
      <div className="p-6 text-center">
        <Spin />
      </div>
    );

  return (
    <div className="space-y-3">
      <TabHeader
        title={t('evaluation_criteria_title')}
        description={t('evaluation_criteria_description')}
      />
      <List
        size="small"
        dataSource={criteria}
        split={false}
        renderItem={(item) => (
          <List.Item
            className="bg-gray-200 rounded-xl !p-4 mb-2 !items-start gap-3"
            data-test="criteria-card"
          >
            <div className="w-full space-y-2">
              {editingId === item.id ? (
                <>
                  <Input
                    value={editedCriteria?.name}
                    onChange={(e) =>
                      setEditedCriteria({
                        ...editedCriteria,
                        name: e.target.value,
                      })
                    }
                    maxLength={50}
                    placeholder={t('evaluation_criteria_name_placeholder')}
                    className="w-full"
                    data-test="criteria-name-input"
                  />
                  <Input.TextArea
                    value={editedCriteria?.description}
                    onChange={(e) =>
                      setEditedCriteria({
                        ...editedCriteria,
                        description: e.target.value,
                      })
                    }
                    rows={4}
                    placeholder={t(
                      'evaluation_criteria_description_placeholder'
                    )}
                    className="w-full"
                  />
                </>
              ) : (
                <>
                  <div className="font-semibold">{item.name}</div>
                  <div>{item.description}</div>
                </>
              )}
            </div>
            <div className="space-y-2">
              {editingId === item.id ? (
                <>
                  <div>
                    <Button
                      type="text"
                      loading={patchLoading || createLoading}
                      icon={<CheckOutlined className="!text-primary-600" />}
                      onClick={() => handleSaveEdit()}
                      data-test="save-criteria-btn"
                    />
                  </div>
                  <Button
                    type="text"
                    icon={<CloseOutlined className="!text-gray-700" />}
                    onClick={handleClickCancel}
                  />
                </>
              ) : (
                <Button
                  type="text"
                  icon={<EditOutlined className="!text-gray-700" />}
                  data-test="edit-criteria-btn"
                  onClick={() =>
                    item.id && handleEdit(item.id, item.name, item.description)
                  }
                />
              )}
            </div>
          </List.Item>
        )}
      />
      <Button
        shape="round"
        icon={<PlusOutlined />}
        onClick={handleClickAdd}
        loading={createLoading}
        disabled={!!editingId}
      >
        {t('evaluation_criteria_add_criteria')}
      </Button>
    </div>
  );
}

export default EvaluationCriteria;
